import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Crown, Lightning, ArrowsClockwise, X, Warning, CheckCircle, Calendar,
  CreditCard, Receipt, ArrowCounterClockwise, Sparkle
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { useSubscription } from '../contexts/SubscriptionContext';
import CancellationReasonModal from '../components/CancellationReasonModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
};

const STATUS_BADGES = {
  active:    { label: 'Active',           bg: 'bg-emerald-500', text: 'text-white' },
  trialing:  { label: 'Free Trial',       bg: 'bg-amber-500',   text: 'text-black' },
  past_due:  { label: 'Past Due',         bg: 'bg-red-500',     text: 'text-white' },
  canceled:  { label: 'Canceled',         bg: 'bg-neutral-500', text: 'text-white' },
  free:      { label: 'Free',             bg: 'bg-neutral-300', text: 'text-neutral-800' },
};

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { plan, planId, subscriptionStatus, currentPeriodEnd, cancelAtPeriodEnd, trialEnd, trialUsed, userType, refresh } = useSubscription();
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    axios.get(`${API_URL}/api/subscription/history`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setHistory(r.data?.events || []))
      .catch(() => {});
  }, [token, navigate]);

  const handleCancel = async ({ reason_code, feedback } = {}) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/subscription/cancel`,
        { reason_code: reason_code || null, feedback: feedback || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Subscription scheduled to end', description: res.data?.message });
      setCancelModalOpen(false);
      await refresh();
      const r = await axios.get(`${API_URL}/api/subscription/history`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(r.data?.events || []);
    } catch (err) {
      toast({ title: 'Could not cancel', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const handleReactivate = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/subscription/reactivate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Welcome back!', description: res.data?.message });
      await refresh();
    } catch (err) {
      toast({ title: 'Could not reactivate', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const status = subscriptionStatus || 'free';
  const statusBadge = STATUS_BADGES[status] || STATUS_BADGES.free;
  const isPaidPlan = planId && planId !== 'free_worker' && planId !== 'starter_contractor';
  const planName = plan?.name || 'Free';
  const trialDays = daysUntil(trialEnd);
  const periodDays = daysUntil(currentPeriodEnd);

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-6">
          <Crown size={32} weight="fill" className="text-[#D95800]" />
          <div>
            <h1 className="text-3xl font-black text-neutral-900 dark:text-white">Subscription</h1>
            <p className="text-sm text-neutral-500">Manage your plan and billing</p>
          </div>
        </div>

        {/* Current Plan Card */}
        <div
          className="bg-white dark:bg-neutral-800 border-4 border-black rounded-2xl p-6 shadow-[6px_6px_0px_#000] dark:shadow-[6px_6px_0px_#D95800] mb-6"
          data-testid="current-plan-card"
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-xs font-black uppercase tracking-wider text-[#D95800] mb-1">Current Plan</div>
              <h2 className="text-3xl font-black text-neutral-900 dark:text-white" data-testid="current-plan-name">{planName}</h2>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-xs font-black uppercase border-2 border-black ${statusBadge.bg} ${statusBadge.text}`}
              data-testid="subscription-status-badge"
            >
              {statusBadge.label}
            </span>
          </div>

          {/* Status info */}
          {status === 'trialing' && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-500 rounded-lg p-3 mb-4 flex items-start gap-2" data-testid="trial-banner">
              <Sparkle size={20} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold text-amber-800 dark:text-amber-200">Free trial — {trialDays} day{trialDays !== 1 ? 's' : ''} remaining</p>
                <p className="text-xs text-amber-700 dark:text-amber-300">Trial ends on {formatDate(trialEnd)}. We'll prompt you to subscribe before then.</p>
              </div>
            </div>
          )}

          {cancelAtPeriodEnd && status !== 'past_due' && (
            <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg p-3 mb-4 flex items-start gap-2" data-testid="cancellation-banner">
              <Warning size={20} weight="fill" className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm flex-1">
                <p className="font-bold text-red-800 dark:text-red-200">{status === 'trialing' ? 'Trial ending — won\'t convert' : 'Subscription ending'}</p>
                <p className="text-xs text-red-700 dark:text-red-300">You'll lose access on {formatDate(currentPeriodEnd)} ({periodDays} day{periodDays !== 1 ? 's' : ''}).</p>
              </div>
              <button
                onClick={handleReactivate}
                disabled={busy}
                className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded-md border-2 border-black shadow-[2px_2px_0px_#000] flex items-center gap-1.5 disabled:opacity-50"
                data-testid="reactivate-btn"
              >
                <ArrowCounterClockwise size={12} weight="bold" />
                Keep My Plan
              </button>
            </div>
          )}

          {status === 'past_due' && (
            <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg p-3 mb-4 flex items-start gap-2" data-testid="past-due-banner">
              <Warning size={20} weight="fill" className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm flex-1">
                <p className="font-bold text-red-800 dark:text-red-200">Payment required</p>
                <p className="text-xs text-red-700 dark:text-red-300">Your subscription expired. Renew to restore full access.</p>
              </div>
              <button
                onClick={() => navigate('/pricing')}
                className="h-9 px-3 bg-[#D95800] hover:bg-[#B84A00] text-white text-xs font-black rounded-md border-2 border-black shadow-[2px_2px_0px_#000]"
                data-testid="renew-btn"
              >Renew</button>
            </div>
          )}

          {/* Period info */}
          <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
            <div className="bg-neutral-100 dark:bg-neutral-900 rounded-lg p-3 border border-neutral-200 dark:border-neutral-700">
              <div className="text-[10px] font-black uppercase text-neutral-500 mb-1 flex items-center gap-1">
                <Calendar size={10} weight="bold" />
                {status === 'trialing' ? 'Trial Ends' : 'Renews On'}
              </div>
              <div className="font-bold text-neutral-900 dark:text-white" data-testid="period-end-date">{formatDate(currentPeriodEnd)}</div>
            </div>
            <div className="bg-neutral-100 dark:bg-neutral-900 rounded-lg p-3 border border-neutral-200 dark:border-neutral-700">
              <div className="text-[10px] font-black uppercase text-neutral-500 mb-1 flex items-center gap-1">
                <Receipt size={10} weight="bold" />Plan ID
              </div>
              <div className="font-mono text-xs text-neutral-700 dark:text-neutral-300">{planId || 'free'}</div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {!isPaidPlan && !trialUsed && userType !== 'homeowner' && (
              <button
                onClick={() => navigate('/pricing')}
                className="h-11 px-5 bg-[#D95800] hover:bg-[#B84A00] text-white font-black rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all flex items-center gap-2"
                data-testid="upgrade-btn"
              >
                <Sparkle size={14} weight="fill" />
                Start Free Trial
              </button>
            )}
            {!isPaidPlan && (
              <button
                onClick={() => navigate('/pricing')}
                className="h-11 px-5 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-black rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all flex items-center gap-2"
                data-testid="view-plans-btn"
              >
                <Crown size={14} weight="fill" />
                View Plans
              </button>
            )}
            {isPaidPlan && (
              <>
                <button
                  onClick={() => navigate('/pricing')}
                  className="h-11 px-5 bg-[#2B4C6F] hover:bg-[#203A55] text-white font-black rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all flex items-center gap-2"
                  data-testid="change-plan-btn"
                >
                  <ArrowsClockwise size={14} weight="bold" />
                  Change Plan
                </button>
                {!cancelAtPeriodEnd && status !== 'past_due' && (
                  <button
                    onClick={() => setCancelModalOpen(true)}
                    disabled={busy}
                    className="h-11 px-5 bg-white dark:bg-neutral-700 text-red-600 dark:text-red-400 font-bold rounded-lg border-2 border-red-500 transition-all flex items-center gap-2 disabled:opacity-50"
                    data-testid="cancel-btn"
                  >
                    <X size={14} weight="bold" />
                    Cancel
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* History */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-2xl p-6 shadow-[4px_4px_0px_#000]" data-testid="subscription-history">
          <h3 className="text-lg font-black text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
            <Receipt size={18} weight="bold" /> Activity Log
          </h3>
          {history.length === 0 ? (
            <p className="text-sm text-neutral-500 italic">No subscription activity yet.</p>
          ) : (
            <div className="space-y-2">
              {history.map((ev, idx) => (
                <div key={ev._id || idx} className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700" data-testid={`history-event-${idx}`}>
                  <div>
                    <p className="text-sm font-bold text-neutral-900 dark:text-white capitalize">{(ev.event || '').replace(/_/g, ' ')}</p>
                    {ev.plan_id && <p className="text-xs text-neutral-500">Plan: {ev.plan_id}</p>}
                  </div>
                  <span className="text-xs text-neutral-500">{formatDate(ev.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* FAQ */}
        <div className="mt-6 text-xs text-neutral-500 dark:text-neutral-400 space-y-1">
          <p>• Free trial doesn't require a credit card and converts only when you choose to subscribe.</p>
          <p>• Canceling keeps access until the end of your current period — no immediate loss.</p>
          <p>• Renewal reminders are sent 7 and 3 days before each cycle.</p>
        </div>
      </div>

      {/* Cancellation reason micro-survey */}
      <CancellationReasonModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={handleCancel}
        busy={busy}
        planName={plan?.name || 'your plan'}
      />
    </div>
  );
}
